import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill } from "@/components/shared/Common";
import MatchScoreCard from "@/components/shared/MatchScoreCard";
import PerformanceReviewForm from "@/components/shared/PerformanceReviewForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { COMPANY } from "@/constants/testIds";
import { CreditCard, Phone, Mail, User, CalendarClock, AlertOctagon, Users2 } from "lucide-react";

function NoShowReportButton({ bookingId, onDone }) {
  const REASONS = [
    { v: "no_show", l: "Did not turn up" },
    { v: "left_early", l: "Left the site early" },
    { v: "wrong_kit", l: "Arrived without required kit / uniform" },
    { v: "behavioural", l: "Behavioural or conduct issue" },
    { v: "team_short", l: "Team failed to deliver full headcount" },
    { v: "unresponsive", l: "Unreachable on the day" },
    { v: "other", l: "Other (see notes)" },
  ];
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("no_show");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${bookingId}/report-no-show`, { reason, notes });
      toast.success("Report filed. Admin notified. Pro flagged.");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-red-500 text-red-600 hover:bg-red-50" data-testid="booking-report-noshow-btn">
          <AlertOctagon className="w-4 h-4 mr-2" /> Report no-show / contract failure
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Report contract failure</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Reason</Label>
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full h-10 rounded-md border border-white/10 bg-[#0A0A0A] px-3 text-sm font-manrope" data-testid="noshow-reason">
              {REASONS.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What happened, timestamps, replacement action…" data-testid="noshow-notes" />
          </div>
          <div className="text-xs text-white/60 font-manrope">
            The pro/team will be automatically flagged (Strike 1 minimum). Admin is emailed. The booking is locked so it can&apos;t be marked complete.
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="noshow-submit">
            {busy ? "Filing…" : "File no-show report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReplacementFinder({ bookingId }) {
  const [suggestions, setSuggestions] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/bookings/${bookingId}/replacement-suggestions`);
      setSuggestions(data.suggestions || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3">
      {!suggestions ? (
        <Button size="sm" onClick={load} disabled={busy} className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="replacement-find">
          <Users2 className="w-3.5 h-3.5 mr-2" /> {busy ? "Finding…" : "Find replacement pros"}
        </Button>
      ) : suggestions.length === 0 ? (
        <div className="text-sm text-white/70 font-manrope">No available pros match right now — try broader filters on the Search page.</div>
      ) : (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-widest text-white font-outfit font-bold">Top {suggestions.length} replacement pros</div>
          {suggestions.slice(0, 5).map((p) => (
            <div key={p.id} className="bg-[#0A0A0A] rounded border border-white/10 p-3 flex items-center justify-between gap-3" data-testid={`replacement-${p.secusource_id}`}>
              <div className="min-w-0">
                <div className="font-outfit font-semibold text-white">{p.secusource_id} · <span className="text-white/60 font-manrope text-sm">{p.county}</span></div>
                <div className="text-xs text-white/60 font-manrope">{(p.role_categories || []).slice(0, 2).join(", ")} · Rating {p.rating || "—"} · {p.completed_jobs || 0} completed jobs</div>
              </div>
              <Button asChild size="sm" className="bg-brand hover:bg-brand-hover text-white flex-shrink-0">
                <a href={`/company/professionals/${p.id}`} data-testid={`replacement-view-${p.secusource_id}`}>View</a>
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function CompanyBookingDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [b, setB] = useState(null);
  const [profMatch, setProfMatch] = useState(null);
  const [paying, setPaying] = useState(false);
  const [pollMsg, setPollMsg] = useState("");
  const [call, setCall] = useState({ suggested_time: "", notes: "", participants: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/bookings/${id}`);
      setB(data);
      try {
        const m = await api.get(`/bookings/${id}/match`);
        setProfMatch(m.data);
      } catch { setProfMatch(null); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Scroll to the review form when the URL includes #review (from CompanyBookings "Write review" links)
  const bookingLocation = useLocation();
  useEffect(() => {
    if (b && bookingLocation.hash === "#review") {
      const el = document.getElementById("review");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, [b, bookingLocation.hash]);

  // Poll payment status if session_id in URL
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    setPollMsg("Verifying payment…");
    const poll = async () => {
      attempts += 1;
      if (attempts > 8) { setPollMsg("Payment verification timed out. Refresh in a moment."); return; }
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setPollMsg("Payment successful. Contact details unlocked.");
          params.delete("session_id");
          setParams(params, { replace: true });
          await load();
          return;
        }
        if (data.status === "expired") { setPollMsg("Payment session expired."); return; }
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
     
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async () => {
    setPaying(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post(`/payments/checkout/${id}`, {}, { headers: { origin } });
      if (data.fully_covered) {
        toast.success("Fully covered by your referral credit — booking unlocked");
        await load();
        return;
      }
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPaying(false); }
  };

  const scheduleCall = async () => {
    try {
      await api.post("/group-calls", {
        booking_id: id,
        suggested_time: call.suggested_time,
        notes: call.notes,
        participants: call.participants.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Group call requested");
      setCall({ suggested_time: "", notes: "", participants: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const complete = async () => {
    try { await api.post(`/bookings/${id}/complete`); toast.success("Booking marked complete"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!b) return <div className="text-white/60">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-outfit font-bold text-white">Booking Detail</h1>
          <div className="text-white/60 font-manrope">Booking ID: <code className="text-sm">{b.id}</code></div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {b.status !== "completed" && !b.no_show_report && b.status !== "cancelled" && (
            <button onClick={complete} className="btn-ghost-dark" data-testid="btn-mark-completed">
              Mark job completed
            </button>
          )}
          {b.ops_room_option && b.ops_room_option !== "none" && (
            <a href={`/company/bookings/${id}/assign-roles`} className="btn-brand" data-testid="btn-assign-ops-roles">
              Assign Ops Roles →
            </a>
          )}
        </div>
      </div>

      {pollMsg && <div className="card-flat p-4 border-l-4 border-l-brand text-sm font-manrope">{pollMsg}</div>}

      {profMatch && (
        <div data-testid="booking-match-block">
          <MatchScoreCard match={profMatch} size="md" showBreakdown={true} />
        </div>
      )}

      <div className="card-flat p-6 grid md:grid-cols-2 gap-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-2">Details</div>
          <div className="text-sm space-y-1 font-manrope text-white/85">
            <div>Type: {b.target_type}</div>
            <div>Date: {b.date || "—"}</div>
            <div>Workers: {b.workers}</div>
            <div>Hours: {b.hours}</div>
            <div>Hourly rate: €{b.hourly_rate}</div>
            <div>Notes: {b.notes || "—"}</div>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-2">Fee</div>
          <div className="bg-navy-900 text-white p-4 rounded">
            <div className="text-xs uppercase tracking-widest text-white/50 font-outfit">Applied Fee</div>
            <div className="text-3xl font-outfit font-bold">€{b.fee?.applied_fee}</div>
            <div className="text-xs text-white/50 font-manrope">({b.fee?.fee_method})</div>
            {b.fee?.credit_applied > 0 && (
              <div className="mt-2 pt-2 border-t border-white/10">
                <div className="text-xs text-gold-400 font-manrope">Referral credit: −€{b.fee.credit_applied}</div>
                <div className="text-lg font-outfit font-bold text-gold-400">Net €{b.fee.net_fee}</div>
              </div>
            )}
          </div>
          <div className="mt-3 flex gap-2 items-center">
            <StatusPill status={b.status} />
            <span className="text-xs font-manrope text-white/60">Payment: <strong className={b.payment_status === "paid" ? "text-gold-500" : "text-amber-600"}>{b.payment_status}</strong></span>
          </div>
        </div>
      </div>

      {b.payment_status !== "paid" ? (
        <div className="card-flat p-6">
          <h2 className="text-xl font-outfit font-semibold text-white mb-2">Unlock Contact Details</h2>
          <p className="text-white/70 font-manrope text-sm mb-4">Pay the SecuSource fee via Stripe to unlock the professional&apos;s identity and contact.</p>
          <Button onClick={pay} disabled={paying} className="bg-brand hover:bg-brand-hover text-white py-6 px-6" data-testid={COMPANY.payFee}>
            <CreditCard className="w-4 h-4 mr-2" /> {paying ? "Redirecting…" : `Pay €${b.fee?.net_fee ?? b.fee?.applied_fee} & Unlock`}
          </Button>
        </div>
      ) : (
        <div className="card-flat p-6 border-l-4 border-l-gold-500">
          <h2 className="text-xl font-outfit font-semibold text-white mb-3">Contact Unlocked</h2>
          {b.unlocked_contact ? (
            <div className="space-y-2 text-sm font-manrope text-white">
              <div className="flex items-center gap-2"><User className="w-4 h-4 text-brand" />{b.unlocked_contact.full_name}</div>
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-brand" />{b.unlocked_contact.email}</div>
              <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-brand" />{b.unlocked_contact.phone}</div>
            </div>
          ) : (<div className="text-white/60 font-manrope">Loading contact…</div>)}

          <div className="mt-6 border-t pt-6">
            <h3 className="font-outfit font-semibold text-white mb-2 flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Schedule Group Call</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div><Label>Suggested time</Label><Input type="datetime-local" value={call.suggested_time} onChange={(e) => setCall({ ...call, suggested_time: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Participants (comma-separated emails)</Label><Input value={call.participants} onChange={(e) => setCall({ ...call, participants: e.target.value })} /></div>
              <div className="md:col-span-3"><Label>Notes</Label><Textarea rows={2} value={call.notes} onChange={(e) => setCall({ ...call, notes: e.target.value })} /></div>
            </div>
            <Button onClick={scheduleCall} className="mt-3 bg-brand hover:bg-brand-hover text-white">Request Call</Button>
          </div>

          {b.status !== "completed" && (
            <div className="mt-6 border-t pt-6 flex flex-wrap gap-3">
              <Button onClick={complete} variant="outline" className="border-gold-500 text-gold-600 hover:bg-gold-50" data-testid="booking-complete-btn">Mark Job Completed</Button>
              {!b.no_show_report && (
                <NoShowReportButton bookingId={b.id} onDone={load} />
              )}
            </div>
          )}

          {b.no_show_report && (
            <div className="mt-6 border-l-4 border-l-red-500 bg-red-50 rounded p-4 space-y-2" data-testid="no-show-notice">
              <div className="text-xs uppercase tracking-widest text-red-700 font-outfit font-bold flex items-center gap-1"><AlertOctagon className="w-3.5 h-3.5" /> No-show / contract failure filed</div>
              <div className="text-sm text-white font-manrope"><b>Reason:</b> {b.no_show_report.reason_label}</div>
              {b.no_show_report.notes && <div className="text-sm text-white/70 font-manrope italic">&ldquo;{b.no_show_report.notes}&rdquo;</div>}
              <div className="text-xs text-white/60 font-manrope">Filed {new Date(b.no_show_report.reported_at).toLocaleString()} — SecuSource admin has been notified.</div>
              <ReplacementFinder bookingId={b.id} />
            </div>
          )}

          {b.status === "completed" && (
            <div id="review">
              <PerformanceReviewForm booking={b} onDone={load} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* Legacy endorsement block retained below for reference. */
function EndorsementBlock({ booking, onDone }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const submit = async () => {
    try {
      await api.post("/endorsements", {
        target_type: booking.target_type,
        target_id: booking.target_id,
        booking_id: booking.id,
        rating: Number(rating),
        written: text,
      });
      toast.success("Endorsement submitted");
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="mt-6 border-t pt-6">
      <h3 className="font-outfit font-semibold text-white mb-2">Endorse this professional</h3>
      <div className="grid md:grid-cols-4 gap-3 items-end">
        <div>
          <Label>Rating (1–5)</Label>
          <Input type="number" min={1} max={5} value={rating} onChange={(e) => setRating(e.target.value)} />
        </div>
        <div className="md:col-span-3"><Label>Written endorsement</Label><Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} /></div>
      </div>
      <Button onClick={submit} className="mt-3 bg-brand hover:bg-brand-hover text-white" data-testid="endorsement-submit-btn">Submit Endorsement</Button>
    </div>
  );
}
